import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { Modal, ProgressBar, Table, Spinner, FormControl, Alert } from 'react-bootstrap';

/* ──────────────────────────────────────────────────────────────
   DESIGN TOKENS — Identik dengan TrsLaboratorium
────────────────────────────────────────────────────────────── */
const T = {
  navH  : 50,
  radius: 10,
  border: '#e2e8f0',
  muted : '#94a3b8',
  navy  : '#0f172a',
  blue  : '#1e3a5f',
  sky   : '#0ea5e9',
  teal  : '#0f766e',
  tealM : '#0d9488',
  slate : '#f8fafc',
  amber : '#d97706',
};

const fmt = (v) => (parseFloat(v) || 0).toLocaleString('id-ID');
const getApiUrl = () => localStorage.getItem('apiUrl') || 'http://localhost:8000';

/* ──────────────────────────────────────────────────────────────
   SUB-COMPONENTS — identik dengan TrsLaboratorium
────────────────────────────────────────────────────────────── */

/** Baris form label + input */
const FormRow = ({ label, icon, children, last }) => (
  <div style={{
    display: 'flex', alignItems: 'stretch',
    borderBottom: last ? 'none' : `1px solid ${T.border}`,
    overflow: 'hidden',
  }}>
    <div style={{
      background: 'transparent', color: '#475569',
      fontSize: '0.695rem', fontWeight: 700,
      letterSpacing: '0.35px', whiteSpace: 'nowrap',
      padding: '0 12px', minWidth: 168,
      display: 'flex', alignItems: 'center', gap: 6,
      borderRight: `1px solid ${T.border}`, flexShrink: 0,
    }}>
      {icon && <i className={`bi ${icon}`} style={{ color: T.sky, fontSize: '0.85rem' }} />}
      {label}
    </div>
    {children}
  </div>
);

/** Input teks di dalam FormRow */
const FInput = React.forwardRef(({ divider, flex, width, style, ...props }, ref) => (
  <input
    ref={ref}
    className="form-control"
    style={{
      height: 32, fontSize: '0.8rem', border: 'none',
      borderRight: divider ? `1px solid ${T.border}` : 'none',
      borderRadius: 0, padding: '0 10px', outline: 'none',
      background: '#fff', color: '#1e293b',
      flex: flex ?? 1,
      width: width, minWidth: width,
      ...style
    }}
    {...props}
  />
));

/** Section card dengan border & judul */
const Section = ({ title, icon, iconColor = T.sky, children, style = {} }) => (
  <div style={{
    background: '#fff', borderRadius: T.radius,
    border: `1px solid ${T.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden', ...style
  }}>
    <div style={{
      background: T.slate,
      borderBottom: `1px solid ${T.border}`,
      padding: '6px 14px',
      display: 'flex', alignItems: 'center', gap: 7,
    }}>
      <i className={`bi ${icon}`} style={{ color: iconColor, fontSize: '0.9rem' }} />
      <span style={{ fontWeight: 700, fontSize: '0.71rem', color: '#475569', letterSpacing: '0.7px' }}>
        {title}
      </span>
    </div>
    {children}
  </div>
);

/* ──────────────────────────────────────────────────────────────
   MAIN COMPONENT
────────────────────────────────────────────────────────────── */
export default function TrsApotek() {
  const navigate  = useNavigate();
  const kirsRef   = useRef(null);
  const user      = localStorage.getItem('username') || 'Petugas Apotek';

  /* ── State: Clock ── */
  const [time, setTime] = useState({ date: '', clock: '' });

  /* ── State: Jenis Transaksi (dari API) ── */
  const [jenisOpts, setJenisOpts] = useState([]);
  const [jenis, setJenis]         = useState(null);

  /* ── State: Sumber Pasien ── */
  const sumberPasienOpts = [
    { value: '1', label: '1. Pasien Rawat Jalan' },
    { value: '2', label: '2. Pasien Rawat Inap' },
    { value: '3', label: '3. Pasien Bebas / Apotek Umum' },
  ];
  const [sumberPasien, setSumberPasien] = useState(null);

  /* ── State: Pasien ── */
  const [pasien, setPasien] = useState({
    kirs: '', nama: '', klasifikasi: '', usia: '',
    dokter: '', no_resep: '', kelas: '', ruang: ''
  });

  /* ── State: Administrasi Resep ── */
  const [adminResep, setAdminResep] = useState(0);

  /* ── State: Baris item obat ── */
  const [rows, setRows] = useState([]);

  /* ── State: Input baris obat ── */
  const [inputRow, setInputRow] = useState({
    kd_obat: '', nm_obat: '', qty: 1,
    satuan: '', harga: 0, mrgn: 0,
    embalase: 1000, total: 0
  });

  /* ── State: Modals ── */
  const [showCariObat,   setShowCariObat]   = useState(false);
  const [obatData, setObatData]             = useState([]);
  const [obatFiltered, setObatFiltered]     = useState([]);
  const [obatLoading, setObatLoading]       = useState(false);
  const [obatSearchModal, setObatSearchModal] = useState('');

  const [showCariPasien, setShowCariPasien] = useState(false);
  const [pasienData, setPasienData]         = useState([]);
  const [pasienFiltered, setPasienFiltered] = useState([]);
  const [pasienLoading, setPasienLoading]   = useState(false);
  const [pasienSearchModal, setPasienSearchModal] = useState('');

  const [showProses,     setShowProses]     = useState(false);
  const [dialog, setDialog] = useState({ show: false, title: '', msg: '', type: 'alert' });
  const [pdfUrl, setPdfUrl] = useState('');
  const [printProgress, setPrintProgress] = useState(0);
  const [laporanDialog, setLaporanDialog] = useState({ show: false, tgl1: '', tgl2: '', loading: false });
  const [confirmDialog, setConfirmDialog] = useState({ show: false, msg: '', onConfirm: null });

  /* ── State: Cetak Ulang Nota ── */

  const [showCetakUlang, setShowCetakUlang]   = useState(false);
  const [inputNota,      setInputNota]         = useState('');
  const [notaLoading,    setNotaLoading]       = useState(false);
  const [notaResult,     setNotaResult]        = useState(null);   // { header, items, grand_total }
  const notaInputRef = useRef(null);
  const qtyInputRef  = useRef(null);
  const nmObatInputRef = useRef(null);

  /* ── State: Dokter opts ── */
  const [dokterOpts, setDokterOpts] = useState([]);

  /* ── Toast ── */
  const [toast, setToast] = useState({ show: false, msg: '', variant: 'success' });
  const showToast = (msg, variant = 'success') => {
    setToast({ show: true, msg, variant });
    setTimeout(() => setToast({ show: false, msg: '', variant: 'success' }), 3500);
  };

  /* ── Effects ── */

  // Clock
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setTime({
        date : n.toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }),
        clock: n.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch Jenis Transaksi dari API: /jnstrxapt → { jnstrx_apt: [{kode, nama}] }
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${getApiUrl()}/jnstrxapt`);
        const data = await res.json();
        const arr = data.jnstrx_apt || [];
        setJenisOpts(arr.map(x => ({ value: x.kode, label: x.nama })));
        if (arr.length > 0) setJenis({ value: arr[0].kode, label: arr[0].nama });
      } catch {
        // Jika API tidak tersedia, tampilkan opsi default
        const fallback = [
          { value: '01', label: 'PENJUALAN RESEP RAWAT JALAN' },
          { value: '02', label: 'PENJUALAN RESEP RAWAT INAP' },
          { value: '03', label: 'PENJUALAN BEBAS / APOTEK UMUM' },
          { value: '04', label: 'PENJUALAN RESEP IGD / UGD' },
          { value: '05', label: 'PENGELUARAN OBAT KARYAWAN' },
        ];
        setJenisOpts(fallback);
        setJenis(fallback[0]);
      }
    };
    load();
  }, []);

  // Fetch Dokter
  useEffect(() => {
    fetch(`${getApiUrl()}/show_dokter`)
      .then(r => r.json())
      .then(d => {
        if (d.list_dokter) setDokterOpts(d.list_dokter.map(x => ({ value: x.nodok, label: x.nama_dok })));
      })
      .catch(() => {});
  }, []);

  /* ── Helper: apakah jenis transaksi yang dipilih adalah CETAK ULANG? ──
     Deteksi berdasarkan nama label (case-insensitive) atau value '07' / '7'      */
  const isCetakUlang = (opt) => {
    if (!opt) return false;
    const lbl = (opt.label || '').toUpperCase();
    const val = String(opt.value || '');
    return (
      lbl.includes('CETAK ULANG') ||
      lbl.includes('ULANG NOTA')  ||
      val === '07' || val === '7'
    );
  };

  /* ── Handler: jenis transaksi berubah ── */
  const handleJenisChange = (sel) => {
    setJenis(sel);
    if (sel && isCetakUlang(sel)) {
      // Buka modal cetak ulang setelah render
      setTimeout(() => {
        setInputNota('');
        setNotaResult(null);
        setShowCetakUlang(true);
        setTimeout(() => notaInputRef.current && notaInputRef.current.focus(), 100);
      }, 50);
    }
  };

  /* ── Handler: fetch & tampilkan nota dari API ── */
  const handleFetchNota = async () => {
    const noNota = inputNota.trim();
    if (!noNota) { showToast('Masukkan nomor nota terlebih dahulu!', 'warning'); return; }
    setNotaLoading(true);
    setNotaResult(null);
    try {
      const res  = await fetch(`${getApiUrl()}/cetak_ulang_nota_apotek?no_nota=${encodeURIComponent(noNota)}`);
      const data = await res.json();
      if (data.status === 1) {
        setNotaResult(data);
        showToast(`Nota ditemukan: ${data.items.length} item obat`, 'success');
      } else {
        setDialog({ show: true, title: 'Nota Tidak Ditemukan', msg: data.msg || 'Nomor nota tidak ada dalam database.', type: 'alert' });
      }
    } catch (err) {
      setDialog({ show: true, title: 'Error Koneksi', msg: 'Gagal menghubungi server: ' + err.message, type: 'alert' });
    } finally {
      setNotaLoading(false);
    }
  };

  /* ── Handler: cetak nota (print browser window) ── */
  const handleCetakUlangPrint = () => {
    if (!notaResult) return;
    const { header, items, grand_total } = notaResult;
    const fmtNum = (v) => (parseFloat(v) || 0).toLocaleString('id-ID');
    const isBpjs = (header.kd_klasifikasi || '').startsWith('05');
    const judulKelas = (header.kd_klas === '001') 
      ? 'NOTA PENJUALAN INSTALASI FARMASI RAWAT JALAN'
      : 'NOTA PENJUALAN INSTALASI FARMASI RAWAT INAP';

    const itemRows = items.map((item, idx) => `
      <tr style="border-bottom:1px solid #e2e8f0">
        <td style="padding:4px 8px;text-align:center">${idx + 1}</td>
        <td style="padding:4px 8px">${item.nm_obat}</td>
        <td style="padding:4px 8px;text-align:center">${fmtNum(item.qty)}</td>
        <td style="padding:4px 8px;text-align:center">${item.satuan}</td>
        <td style="padding:4px 8px;text-align:center">${isBpjs ? item.dijamin_label : '-'}</td>
        <td style="padding:4px 8px;text-align:right">${fmtNum(item.total)}</td>
      </tr>
      ${item.prinsipal ? `<tr><td></td><td style="padding:2px 8px;color:#6b7280;font-size:0.78rem" colspan="5">${item.prinsipal}</td></tr>` : ''}
      ${(item.selisih > 0 || item.subsidi > 0) ? `
        <tr style="background:#f0fdf4"><td></td>
          <td style="padding:2px 8px;color:#16a34a;font-size:0.8rem" colspan="4">Dijamin: ${item.nm_obat2 || '-'}</td>
          <td style="padding:2px 8px;text-align:right;color:#16a34a">+${fmtNum(item.total2)}</td>
        </tr>
        <tr style="background:#fef9c3"><td></td>
          <td style="padding:2px 8px;color:#92400e;font-size:0.8rem" colspan="4">
            ${item.selisih > 0 ? 'Selisih dibayar Pasien' : 'Selisih disubsidi'}
          </td>
          <td style="padding:2px 8px;text-align:right;color:#92400e">${fmtNum(item.selisih > 0 ? item.selisih : item.subsidi)}</td>
        </tr>` : ''}
    `).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Nota Apotek - ${header.no_nota}</title>
      <style>body{font-family:'Courier New',monospace;font-size:10pt;margin:0;padding:16px}
      table{width:100%;border-collapse:collapse}th{background:#0f172a;color:#fff;padding:6px 8px;font-size:9pt}
      .hdr{font-size:9pt;margin-bottom:4px}.divider{border-top:2px solid #0f172a;margin:6px 0}
      .total-row td{font-weight:bold;padding:5px 8px;border-top:1px solid #0f172a}
      @media print{@page{margin:10mm}}</style>
    </head><body>
      <div class="hdr"><strong>No Nota: ${header.no_nota}</strong> &nbsp;&nbsp; Tanggal: ${header.tgl}</div>
      <div class="divider"></div>
      <div style="text-align:center;font-weight:bold;font-size:11pt;margin:6px 0">${judulKelas}</div>
      <div class="divider"></div>
      <div class="hdr">Pasien &nbsp;&nbsp;&nbsp;: ${header.no_medrec} / ${header.nm_pasien}</div>
      <div class="hdr">Klasifikasi : ${header.nm_klasifikasi || '-'} &nbsp;&nbsp; Kelas: ${header.nm_klas || '-'}</div>
      <div class="hdr">Dokter &nbsp;&nbsp;&nbsp;: ${header.nm_dokter || '-'} &nbsp;&nbsp; No. Resep: ${header.no_resep || '-'}</div>
      <div class="divider"></div>
      <table><thead><tr>
        <th style="width:5%">No.</th>
        <th style="width:38%">Nama Obat</th>
        <th style="width:8%">Qty</th>
        <th style="width:10%">Satuan</th>
        <th style="width:9%">Dijamin</th>
        <th style="width:15%">Harga (Rp)</th>
      </tr></thead><tbody>${itemRows}</tbody>
      <tfoot>
        ${header.admin > 0 ? `<tr class="total-row"><td colspan="5">Administrasi Resep</td><td style="text-align:right">${fmtNum(header.admin)}</td></tr>` : ''}
        <tr class="total-row" style="background:#0f172a;color:#fff"><td colspan="5">TOTAL HARUS DIBAYAR</td><td style="text-align:right">${fmtNum(grand_total)}</td></tr>
      </tfoot></table>
      <div class="divider"></div>
      <div style="font-size:8pt;margin-top:4px">Harga sudah termasuk PPN atau PPN yang dibebaskan / dibayar Pemerintah dan Embalase</div>
      <div style="margin-top:16px;font-size:9pt">Petugas: ${user}</div>
      <script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}</script>
    </body></html>`;
    const win = window.open('', '_blank', 'width=800,height=700');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const hp = (f, v) => setPasien(p => ({ ...p, [f]: v }));

  // Recalculate total baris obat
  const calcTotal = (row) => {
    const qty      = parseFloat(row.qty)      || 0;
    const harga    = parseFloat(row.harga)    || 0;
    const mrgn     = parseFloat(row.mrgn)     || 0;
    const embalase = parseFloat(row.embalase) || 0;
    return Math.round((harga + harga * mrgn / 100) * qty + embalase);
  };

  const handleInputChange = (field, val) => {
    const updated = { ...inputRow, [field]: val };
    updated.total = calcTotal(updated);
    setInputRow(updated);
  };

  const handleSelectObat = (obat) => {
    const mappedObat = {
      kd_obat: obat.kdbr || obat.kd_obat || '',
      nm_obat: obat.nama || obat.nm_obat || '',
      satuan: obat.satuanbeli || obat.satuan || '',
      harga: obat.hda || obat.harga || 0,
      stok: obat.stok || 0,
      mrgn: obat.mrgn || 0,
      embalase: obat.embalase || 0
    };

    const exists = rows.find(r => r.kd_obat === mappedObat.kd_obat);
    if (exists) {
      setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: `Obat ${mappedObat.nm_obat} sudah ada di daftar!` });
      return;
    }

    const updated = {
      ...inputRow,
      kd_obat: mappedObat.kd_obat,
      nm_obat: mappedObat.nm_obat,
      satuan: mappedObat.satuan,
      harga: mappedObat.harga,
      stok: mappedObat.stok
    };
    updated.total = calcTotal(updated);
    setInputRow(updated);
    setShowCariObat(false);
    showToast(`Obat dipilih: ${mappedObat.nm_obat}`, 'info');

    setTimeout(() => {
      if (qtyInputRef.current) {
        qtyInputRef.current.focus();
        qtyInputRef.current.select();
      }
    }, 150);
  };

  const handleAddRow = () => {
    if (!inputRow.nm_obat) { showToast('Pilih obat terlebih dahulu!', 'warning'); return; }
    if ((parseFloat(inputRow.qty) || 0) <= 0) { showToast('Jumlah harus lebih dari 0!', 'warning'); return; }
    setRows(r => [...r, { id: Date.now(), ...inputRow }]);
    setInputRow({ kd_obat: '', nm_obat: '', qty: 1, satuan: '', harga: 0, mrgn: 0, embalase: 1000, total: 0 });
    showToast('Item obat ditambahkan', 'success');

    setTimeout(() => {
      if (nmObatInputRef.current) {
        nmObatInputRef.current.focus();
      }
    }, 100);
  };

  const handleDeleteRow = (id) => {
    setConfirmDialog({
      show: true,
      msg: 'Hapus item obat ini dari transaksi?',
      onConfirm: () => {
        setRows(r => r.filter(x => x.id !== id));
        showToast('Item dihapus', 'info');
        setConfirmDialog({ show: false, msg: '', onConfirm: null });
      }
    });
  };

  const handleSelectPasien = (p) => {
    setPasien({ 
      kirs: p.no_medrec || p.kirs || '', 
      nama: p.nama || '', 
      klasifikasi: p.nm_klasifikasi || p.kd_klasifikasi || p.penjamin || p.klasifikasi || '', 
      usia: p.umur || p.usia || '', 
      dokter: p.nama_dok || p.nama_dokter || p.dokter || '', 
      no_resep: p.no_resep || '', 
      kelas: p.nm_klas || p.kd_klas || p.kelas || '', 
      ruang: p.no_kamar || p.ruangRawat || p.nm_ruang || p.ruang || '' 
    });
    setShowCariPasien(false);
    showToast(`Pasien dimuat: ${p.nama || ''}`, 'success');
  };

  const handleProses = () => {
    if (rows.length === 0) { setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: 'Belum ada item obat dalam transaksi!' }); return; }
    if (!pasien.kirs)     { setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: 'Silakan pilih pasien terlebih dahulu!' }); return; }
    setShowProses(true);
  };

  const handleReset = () => {
    setConfirmDialog({
      show: true,
      msg: 'Bersihkan semua data transaksi ini?',
      onConfirm: () => {
        setRows([]);
        setAdminResep(0);
        setPasien({ kirs: '', nama: '', klasifikasi: '', usia: '', dokter: '', no_resep: '', kelas: '', ruang: '' });
        showToast('Form transaksi dibersihkan', 'info');
        setConfirmDialog({ show: false, msg: '', onConfirm: null });
      }
    });
  };

  const handleCetakLaporan = async () => {
    setLaporanDialog(p => ({ ...p, loading: true }));
    let prog = 10; setPrintProgress(prog);
    const iv = setInterval(() => { prog = Math.min(prog + 15, 85); setPrintProgress(prog); }, 100);
    try {
      const res = await fetch(`${getApiUrl()}/laporan_harian_apotek`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tgl1: laporanDialog.tgl1, tgl2: laporanDialog.tgl2 })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      clearInterval(iv); setPrintProgress(100);
      setTimeout(() => { setPrintProgress(0); setPdfUrl(url); setLaporanDialog(p => ({ ...p, show: false, loading: false })); }, 500);
    } catch (err) {
      clearInterval(iv); setPrintProgress(0);
      setDialog({ show: true, type: 'alert', title: 'Error', msg: 'Gagal mencetak laporan: ' + err.message });
      setLaporanDialog(p => ({ ...p, loading: false }));
    }
  };

  /* ── Computed totals ── */
  const subTotal   = useMemo(() => rows.reduce((s, r) => s + (r.total || 0), 0), [rows]);
  const grandTotal = useMemo(() => subTotal + (parseFloat(adminResep) || 0), [subTotal, adminResep]);

  const handlePasienSearch = (e) => {
    const val = e.target.value;
    setPasienSearchModal(val);
    const q = val.toLowerCase();
    const filtered = pasienData.filter(d => 
      (d.nama && d.nama.toLowerCase().includes(q)) || 
      (d.no_medrec && d.no_medrec.toLowerCase().includes(q))
    );
    setPasienFiltered(filtered);
  };

  const openPasienModal = async (tipeVal) => {
    setShowCariPasien(true);
    setPasienLoading(true);
    setPasienData([]);
    setPasienFiltered([]);
    setPasienSearchModal('');

    let endpoint = '';
    if (tipeVal === '1') endpoint = '/show_pasienrj';
    else if (tipeVal === '2') endpoint = '/show_pasienri';
    else if (tipeVal === '3') endpoint = '/show_pasientt';
    else endpoint = '/show_pasienrj'; // default

    try {
      const res = await fetch(`${getApiUrl()}${endpoint}`);
      const data = await res.json();
      let arr = [];
      if (Array.isArray(data)) arr = data;
      else if (data.list_pasienrj) arr = data.list_pasienrj;
      else if (data.list_pasienri) arr = data.list_pasienri;
      else if (data.data) arr = data.data;
      setPasienData(arr);
      setPasienFiltered(arr);
    } catch (err) { console.error(err); }
    finally { setPasienLoading(false); }
  };

  const handleObatSearch = (e) => {
    const val = e.target.value;
    setObatSearchModal(val);
    const q = val.toLowerCase();
    const filtered = obatData.filter(d => 
      (d.nama && d.nama.toLowerCase().includes(q)) || 
      (d.nm_obat && d.nm_obat.toLowerCase().includes(q)) || 
      (d.kdbr && d.kdbr.toLowerCase().includes(q)) || 
      (d.kd_obat && d.kd_obat.toLowerCase().includes(q))
    );
    setObatFiltered(filtered);
  };

  const fetchObatData = async (keyword) => {
    setObatLoading(true);
    try {
      const qs = keyword ? `?cariobat=${encodeURIComponent(keyword)}` : '';
      const res = await fetch(`${getApiUrl()}/show_obat${qs}`);
      const data = await res.json();
      const arr = data.list_obat || [];
      setObatData(arr);
      setObatFiltered(arr);
    } catch (err) { console.error(err); }
    finally { setObatLoading(false); }
  };

  const openObatModal = (searchKeyword = '') => {
    setShowCariObat(true);
    setObatSearchModal(searchKeyword);
    fetchObatData(searchKeyword);
  };

  /* ── Column widths untuk tabel obat ── */
  const CW    = ['9%', '26%', '7%', '8%', '9%', '8%', '10%', '11%', '7%', '5%'];
  const CWtot = ['75%', '13%', '7%', '5%'];

  /* ── Action buttons ── */
  const actions = [
    { label: 'PROSES',   icon: 'bi-gear-fill',          bg: `linear-gradient(135deg,${T.blue},${T.sky})`, fg: '#fff', border: 'none', onClick: handleProses },
    { label: 'HAPUS',    icon: 'bi-trash3-fill',         bg: '#fff', fg: '#dc2626', border: '1.5px solid #fca5a5', onClick: handleReset },
    { label: 'DIAGNOSA', icon: 'bi-binoculars-fill',     bg: '#fff', fg: '#7c3aed', border: '1.5px solid #c4b5fd', onClick: () => {} },
    { label: 'NOTA',     icon: 'bi-receipt',             bg: '#fff', fg: '#0ea5e9', border: '1.5px solid #7dd3fc', onClick: () => {} },
    { label: 'LAPORAN',  icon: 'bi-file-earmark-text',   bg: '#fff', fg: '#10b981', border: '1.5px solid #6ee7b7', onClick: () => { const today = new Date().toISOString().split('T')[0]; setLaporanDialog({ show: true, tgl1: today, tgl2: today, loading: false }); } },
    { label: 'BATAL',    icon: 'bi-slash-circle',        bg: '#fff', fg: '#64748b', border: '1.5px solid #cbd5e1', onClick: () => {} },
  ];

  /* ════════════════════════ RENDER ════════════════════════ */
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg,#f0f4f8 0%,#e8f0fe 100%)',
      fontFamily: "'Inter','Segoe UI',sans-serif",
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ══ TOP NAV ══ */}
      <div style={{
        height: T.navH,
        background: `linear-gradient(135deg,${T.navy} 0%,${T.blue} 55%,${T.amber} 180%)`,
        padding: '0 20px', flexShrink: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 2px 16px rgba(0,0,0,0.32)',
      }}>
        {/* Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ background: 'rgba(255,255,255,0.13)', borderRadius: 8, padding: '6px 10px', display: 'flex', alignItems: 'center' }}>
            <i className="bi bi-capsule-pill" style={{ color: '#fbbf24', fontSize: '1.15rem' }} />
          </div>
          <div style={{ lineHeight: 1.35 }}>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.6px' }}>
              TRANSAKSI PENJUALAN OBAT APOTEK
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.68rem' }}>
              Petugas: <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{user}</span>
            </div>
          </div>
        </div>

        {/* Right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: 20, padding: '3px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <i className="bi bi-bag-heart-fill" style={{ color: '#fbbf24', fontSize: '0.82rem' }} />
            <span style={{ color: '#fde68a', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem' }}>Apotek</span>
          </div>
          <div style={{ textAlign: 'right', lineHeight: 1.3 }}>
            <div style={{ color: '#fff', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '1px' }}>{time.clock}</div>
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.65rem' }}>{time.date}</div>
          </div>
          <button
            onClick={() => navigate('/home')}
            style={{
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.28)',
              color: '#fff', borderRadius: 8, padding: '5px 14px',
              fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            <i className="bi bi-house-fill" />Home
          </button>
        </div>
      </div>

      {/* ══ TOAST NOTIFICATION ══ */}
      {toast.show && (
        <Alert 
          variant={toast.variant === 'error' ? 'danger' : toast.variant} 
          onClose={() => setToast({ show: false, msg: '', variant: 'success' })} 
          dismissible
          className="d-flex align-items-center m-0 border-0 rounded-0 shadow-sm"
          style={{ padding: '8px 20px', fontSize: '0.85rem', fontWeight: 600 }}
        >
          <i className={`bi ${toast.variant === 'warning' ? 'bi-exclamation-triangle-fill' : toast.variant === 'danger' ? 'bi-x-circle-fill' : toast.variant === 'info' ? 'bi-info-circle-fill' : 'bi-check-circle-fill'} me-2 fs-5`} />
          <div>{toast.msg}</div>
        </Alert>
      )}

      {/* ══ MAIN CONTENT ══ */}
      <div style={{
        flex: 1, padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto',
      }}>

        {/* ── ROW 1: JENIS TRANSAKSI + SUMBER PASIEN ── */}
        <div style={{ display: 'flex', gap: 10 }}>

          {/* Jenis Transaksi */}
          <div style={{
            flex: 1.4,
            background: 'linear-gradient(90deg, #fff1f2 0%, #ffe4e6 100%)',
            borderRadius: T.radius,
            display: 'flex', alignItems: 'stretch',
            boxShadow: '0 1px 3px rgba(225,29,72,0.1)',
            border: '1px solid #fecdd3', overflow: 'hidden',
          }}>
            <div style={{
              padding: '0 16px', display: 'flex', alignItems: 'center', gap: 8,
              color: '#9f1239', fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.5px',
              borderRight: '1px solid #fecdd3', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <i className="bi bi-list-check" style={{ color: '#e11d48', fontSize: '1rem' }} />
              JENIS TRANSAKSI
            </div>
            <div style={{ flex: 1, padding: '5px 8px' }}>
              <Select
                options={jenisOpts}
                value={jenis}
                onChange={handleJenisChange}
                placeholder={jenisOpts.length === 0 ? 'Memuat jenis transaksi...' : 'Cari atau pilih jenis transaksi...'}
                isSearchable
                isClearable
                menuPortalTarget={document.body}
                formatOptionLabel={o => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      background: T.sky, color: '#fff', borderRadius: 5,
                      padding: '1px 7px', fontSize: '0.68rem', fontWeight: 800,
                      minWidth: 24, textAlign: 'center',
                    }}>{o.value}</span>
                    <span style={{ fontSize: '0.83rem', fontWeight: 600, color: '#1e293b' }}>{o.label}</span>
                  </div>
                )}
                styles={{
                  control: b => ({ ...b, minHeight: 34, height: 34, fontSize: '0.83rem', border: 'none', boxShadow: 'none', background: 'transparent', borderRadius: 6 }),
                  valueContainer: b => ({ ...b, padding: '0 8px' }),
                  indicatorsContainer: b => ({ ...b, height: 34 }),
                  menuPortal: b => ({ ...b, zIndex: 9999 }),
                  option: (b, s) => ({ ...b, fontSize: '0.82rem', background: s.isSelected ? T.sky : s.isFocused ? '#f0f9ff' : '#fff', color: s.isSelected ? '#fff' : '#1e293b' }),
                }}
              />
            </div>
          </div>

          {/* Sumber Pasien */}
          <div style={{
            flex: 1,
            background: 'linear-gradient(90deg, #f0fdf4 0%, #dcfce7 100%)',
            borderRadius: T.radius,
            display: 'flex', alignItems: 'stretch',
            boxShadow: '0 1px 3px rgba(22,163,74,0.1)',
            border: '1px solid #bbf7d0', overflow: 'hidden',
          }}>
            <div style={{
              padding: '0 16px', display: 'flex', alignItems: 'center', gap: 8,
              color: '#166534', fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.5px',
              borderRight: '1px solid #bbf7d0', whiteSpace: 'nowrap', flexShrink: 0,
            }}>
              <i className="bi bi-person-fill-add" style={{ color: '#16a34a', fontSize: '1rem' }} />
              SUMBER PASIEN
            </div>
            <div style={{ flex: 1, padding: '5px 8px' }}>
              <Select
                options={sumberPasienOpts}
                value={sumberPasien}
                onChange={setSumberPasien}
                placeholder="Pilih sumber pasien..."
                isClearable
                menuPortalTarget={document.body}
                styles={{
                  control: b => ({ ...b, minHeight: 34, height: 34, fontSize: '0.83rem', border: 'none', boxShadow: 'none', background: 'transparent', borderRadius: 6 }),
                  valueContainer: b => ({ ...b, padding: '0 8px' }),
                  indicatorsContainer: b => ({ ...b, height: 34 }),
                  menuPortal: b => ({ ...b, zIndex: 9999 }),
                }}
              />
            </div>
          </div>

        </div>

        {/* ── ROW 2: DATA PASIEN + RINGKASAN TOTAL ── */}
        <div style={{ display: 'flex', gap: 10 }}>

          {/* Data Pasien */}
          <Section title="DATA PASIEN & RESEP" icon="bi-person-badge-fill" iconColor={T.sky} style={{ flex: 7 }}>
            <div style={{ display: 'flex', flexDirection: 'column', border: `1px solid ${T.border}`, borderTop: 'none', borderRadius: `0 0 ${T.radius}px ${T.radius}px` }}>

              <FormRow label="KIRS | NAMA PASIEN" icon="bi-credit-card-2-front">
                <FInput
                  ref={kirsRef} flex={0} width={110}
                  placeholder="No. KIRS / RM"
                  value={pasien.kirs}
                  onChange={e => hp('kirs', e.target.value)}
                  divider
                />
                <FInput placeholder="Nama Lengkap Pasien" value={pasien.nama} onChange={e => hp('nama', e.target.value)} divider />
                <button
                  onClick={() => {
                    if (!sumberPasien) {
                      setDialog({ show: true, type: 'alert', title: 'Perhatian', msg: 'Pilih Sumber Pasien terlebih dahulu!' });
                      return;
                    }
                    openPasienModal(sumberPasien.value);
                  }}
                  style={{ flexShrink: 0, background: T.sky, color: '#fff', border: 'none', padding: '0 12px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
                >
                  <i className="bi bi-search" />Cari
                </button>
              </FormRow>

              <FormRow label="DOKTER | NO. RESEP" icon="bi-person-video3">
                <div style={{ flex: 1, borderRight: `1px solid ${T.border}` }}>
                  <Select
                    options={dokterOpts}
                    value={dokterOpts.find(o => o.label === pasien.dokter) || (pasien.dokter ? { label: pasien.dokter, value: pasien.dokter } : null)}
                    onChange={sel => hp('dokter', sel ? sel.label : '')}
                    placeholder="Pilih Dokter Penulis Resep..."
                    isClearable isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      control: b => ({ ...b, minHeight: 32, height: 32, fontSize: '0.8rem', border: 'none', boxShadow: 'none', borderRadius: 0 }),
                      valueContainer: b => ({ ...b, padding: '0 10px' }),
                      indicatorsContainer: b => ({ ...b, height: 32 }),
                      menuPortal: b => ({ ...b, zIndex: 9999 }),
                    }}
                  />
                </div>
                <FInput flex={0} width={150} placeholder="No. Resep" value={pasien.no_resep} onChange={e => hp('no_resep', e.target.value)} />
              </FormRow>

              <FormRow label="KLASIFIKASI | USIA" icon="bi-shield-check" last>
                <FInput placeholder="Klasifikasi (BPJS / Umum / Asuransi...)" value={pasien.klasifikasi} onChange={e => hp('klasifikasi', e.target.value)} divider />
                <FInput flex={0} width={70}  placeholder="Usia" value={pasien.usia}  onChange={e => hp('usia', e.target.value)}  divider style={{ textAlign: 'center' }} />
                <div style={{ background: 'transparent', color: '#475569', fontSize: '0.695rem', fontWeight: 700, letterSpacing: '0.35px', whiteSpace: 'nowrap', padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6, borderRight: `1px solid ${T.border}`, flexShrink: 0 }}>
                  <i className="bi bi-door-open" style={{ color: T.sky, fontSize: '0.85rem' }} /> KELAS | RUANG
                </div>
                <FInput flex={0} width={80}  placeholder="Kelas"   value={pasien.kelas}  onChange={e => hp('kelas', e.target.value)}  divider style={{ textAlign: 'center' }} />
                <FInput placeholder="Ruang Rawat / Poliklinik" value={pasien.ruang} onChange={e => hp('ruang', e.target.value)} />
              </FormRow>

            </div>
          </Section>

          {/* Ringkasan Saldo */}
          <Section title="RINGKASAN TRANSAKSI" icon="bi-cash-coin" iconColor={T.amber} style={{ flex: 3 }}>
            <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Total Obat */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg,#0f172a,#1e3a5f)', borderRadius: 8, padding: '8px 14px' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Total Transaksi Obat</div>
                  <div style={{ color: '#4ade80', fontFamily: 'monospace', fontWeight: 900, fontSize: '1.25rem', letterSpacing: '0.5px' }}>
                    {fmt(subTotal)}
                  </div>
                </div>
                <i className="bi bi-capsule-pill" style={{ color: '#4ade80', fontSize: '1.5rem', opacity: 0.7 }} />
              </div>
              {/* Admin Resep */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ background: '#f8fafc', border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 10px', color: '#475569', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                  Adm. Resep
                </div>
                <input
                  type="number" min={0}
                  value={adminResep}
                  onChange={e => setAdminResep(e.target.value)}
                  style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 6, padding: '5px 10px', fontSize: '0.82rem', fontWeight: 700, textAlign: 'right', outline: 'none', color: '#1e293b' }}
                />
              </div>
              {/* Grand Total */}
              <div style={{ background: `linear-gradient(135deg,${T.teal},${T.tealM})`, borderRadius: 8, padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>Grand Total</div>
                  <div style={{ color: '#fff', fontFamily: 'monospace', fontWeight: 900, fontSize: '1.25rem', letterSpacing: '0.5px' }}>
                    {fmt(grandTotal)}
                  </div>
                </div>
                <i className="bi bi-currency-dollar" style={{ color: '#fff', fontSize: '1.5rem', opacity: 0.7 }} />
              </div>
            </div>
          </Section>

        </div>

        {/* ── TABEL RINCIAN OBAT ── */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: T.radius,
          border: `1px solid ${T.border}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          overflow: 'hidden', minHeight: 200,
        }}>
          {/* Thead */}
          <div style={{ background: `linear-gradient(90deg,${T.teal},${T.tealM})`, flexShrink: 0 }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.75rem', color: '#fff' }}>
              <colgroup>{CW.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
              <thead>
                <tr>
                  <th style={{ padding: '8px 10px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 700 }}>KODE</th>
                  <th style={{ padding: '8px 10px', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 800, letterSpacing: '1px' }}>RINCIAN OBAT / NAMA</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 700 }}>QTY</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 700 }}>SATUAN</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 700 }}>HARGA @</th>
                  <th style={{ padding: '8px 6px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 700 }}>MRGN%</th>
                  <th style={{ padding: '8px 6px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 700 }}>EMBALASE</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right', borderRight: '1px solid rgba(255,255,255,.2)', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 800 }}>TOTAL [Rp]</th>
                  <th colSpan={2} style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.15)', fontWeight: 700 }}>AKSI</th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Tbody scroll */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <colgroup>{CW.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
              <tbody>
                {/* INPUT ROW */}
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #0ea5e9' }}>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      value={inputRow.kd_obat} readOnly
                      className="form-control form-control-sm"
                      placeholder="Kode"
                      style={{ fontSize: '0.74rem', textAlign: 'center', background: '#e2e8f0', cursor: 'not-allowed' }}
                    />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <div style={{ display: 'flex', gap: 3 }}>
                      <input
                        ref={nmObatInputRef}
                        value={inputRow.nm_obat}
                        onChange={e => handleInputChange('nm_obat', e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (inputRow.kd_obat) {
                              handleAddRow();
                            } else {
                              openObatModal(inputRow.nm_obat);
                            }
                          }
                        }}
                        disabled={!pasien.kirs}
                        className="form-control form-control-sm"
                        placeholder="Nama obat..."
                        style={{ fontSize: '0.74rem', flex: 1, backgroundColor: !pasien.kirs ? '#e2e8f0' : '#fff' }}
                      />
                      <button
                        onClick={() => openObatModal(inputRow.nm_obat)}
                        disabled={!pasien.kirs}
                        style={{ background: !pasien.kirs ? '#cbd5e1' : T.sky, color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700, cursor: !pasien.kirs ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                      ><i className="bi bi-search" /></button>
                    </div>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      ref={qtyInputRef}
                      type="number" min={1}
                      value={inputRow.qty}
                      onChange={e => handleInputChange('qty', e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddRow()}
                      disabled={!pasien.kirs}
                      className="form-control form-control-sm text-center"
                      style={{ fontSize: '0.74rem', fontWeight: 700, backgroundColor: !pasien.kirs ? '#e2e8f0' : '#fff' }}
                    />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input value={inputRow.satuan} onChange={e => handleInputChange('satuan', e.target.value)} disabled={!pasien.kirs} className="form-control form-control-sm text-center" style={{ fontSize: '0.74rem', backgroundColor: !pasien.kirs ? '#e2e8f0' : '#fff' }} placeholder="Satuan" />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input type="number" value={inputRow.harga} onChange={e => handleInputChange('harga', e.target.value)} disabled={!pasien.kirs} className="form-control form-control-sm text-end" style={{ fontSize: '0.74rem', backgroundColor: !pasien.kirs ? '#e2e8f0' : '#fff' }} placeholder="Harga" />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input type="number" value={inputRow.mrgn} onChange={e => handleInputChange('mrgn', e.target.value)} disabled={!pasien.kirs} className="form-control form-control-sm text-center" style={{ fontSize: '0.74rem', backgroundColor: !pasien.kirs ? '#e2e8f0' : '#fff' }} placeholder="%" />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input type="number" value={inputRow.embalase} onChange={e => handleInputChange('embalase', e.target.value)} disabled={!pasien.kirs} className="form-control form-control-sm text-end" style={{ fontSize: '0.74rem', backgroundColor: !pasien.kirs ? '#e2e8f0' : '#fff' }} placeholder="Embalase" />
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: T.teal, fontSize: '0.85rem' }}>
                    {fmt(inputRow.total)}
                  </td>
                  <td colSpan={2} style={{ padding: '4px 6px', textAlign: 'center' }}>
                    <button
                      onClick={handleAddRow}
                      disabled={!pasien.kirs}
                      style={{ background: !pasien.kirs ? '#cbd5e1' : '#10b981', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 0', width: '100%', fontSize: '0.74rem', fontWeight: 700, cursor: !pasien.kirs ? 'not-allowed' : 'pointer' }}
                    >
                      <i className="bi bi-plus-lg" /> Tambah
                    </button>
                  </td>
                </tr>

                {/* DATA ROWS */}
                {rows.length > 0 ? rows.map((item, idx) => (
                  <tr key={item.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: '5px 8px', textAlign: 'center', fontFamily: 'monospace', color: T.sky, fontWeight: 700, fontSize: '0.74rem' }}>{item.kd_obat}</td>
                    <td style={{ padding: '5px 8px', color: '#1e293b', fontWeight: 600 }}>{item.nm_obat}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>{item.qty}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center', color: '#64748b' }}>{item.satuan}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: '#334155' }}>{fmt(item.harga)}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'center', color: '#64748b' }}>{item.mrgn}%</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', color: '#64748b' }}>{fmt(item.embalase)}</td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 800, color: T.teal, fontSize: '0.86rem' }}>{fmt(item.total)}</td>
                    <td style={{ padding: '5px 6px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleDeleteRow(item.id)}
                        className="btn btn-sm btn-outline-danger py-0 px-2"
                        style={{ fontSize: '0.72rem' }}
                      ><i className="bi bi-trash" /></button>
                    </td>
                    <td style={{ padding: '5px 6px', textAlign: 'center' }}>
                      <button
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: '0.72rem' }}
                      ><i className="bi bi-pencil" /></button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={10} style={{ textAlign: 'center', padding: '48px 0', color: T.muted }}>
                      <i className="bi bi-bag-x" style={{ fontSize: '2.4rem', opacity: 0.2, display: 'block', marginBottom: 8 }} />
                      <span style={{ fontSize: '0.8rem' }}>Belum ada item obat dalam transaksi</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals Footer */}
          <div style={{ background: T.navy, flexShrink: 0, borderTop: `2px solid ${T.blue}` }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.77rem' }}>
              <colgroup>{CWtot.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
              <tbody>
                {[
                  { label: 'SUB TOTAL OBAT', val: subTotal,    color: '#6ee7b7', bold: false },
                  { label: 'ADMINISTRASI RESEP', val: parseFloat(adminResep) || 0, color: '#fde68a', bold: false },
                  { label: 'GRAND TOTAL',     val: grandTotal, color: '#fca5a5', bold: true  },
                ].map((row, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,.07)', background: row.bold ? 'rgba(255,255,255,.05)' : 'transparent' }}>
                    <td style={{ padding: row.bold ? '9px 16px' : '5px 16px', color: '#e2e8f0', fontWeight: row.bold ? 800 : 600, fontSize: row.bold ? '0.84rem' : '0.76rem', letterSpacing: row.bold ? '0.6px' : 0 }}>
                      {row.label}<span style={{ marginLeft: 6, fontWeight: 300, fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)' }}>[Rp]</span>
                    </td>
                    <td style={{ padding: row.bold ? '9px 14px' : '5px 14px', textAlign: 'right', color: row.color, fontFamily: 'monospace', fontWeight: row.bold ? 800 : 600, fontSize: row.bold ? '0.9rem' : '0.78rem' }}>
                      {fmt(row.val)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── ACTION BUTTONS ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, paddingTop: 2 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {actions.map((btn, i) => (
              <button key={i} onClick={btn.onClick} style={{
                background: btn.bg, color: btn.fg, border: btn.border,
                borderRadius: 8, padding: '7px 16px',
                fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
                boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                letterSpacing: '0.3px',
              }}>
                <i className={`bi ${btn.icon}`} /> {btn.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate('/home')}
            style={{
              background: '#fff', color: '#dc2626',
              border: '1.5px solid #fca5a5', borderRadius: 8,
              padding: '7px 18px', fontWeight: 700, fontSize: '0.78rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            <i className="bi bi-box-arrow-right" /> KELUAR
          </button>
        </div>

      </div>

      {/* ══════════════ MODALS ══════════════ */}

      {/* ─ MODAL CETAK ULANG NOTA ─ */}
      {showCetakUlang && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' }}>

            {/* Header */}
            <div style={{ background: `linear-gradient(135deg,${T.navy},${T.blue})`, padding: '16px 22px', borderRadius: '18px 18px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="bi bi-receipt-cutoff" style={{ color: '#fbbf24', fontSize: '1.3rem' }} />
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: '1rem', letterSpacing: '0.3px' }}>Cetak Ulang Nota Apotek</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>Masukkan nomor nota yang akan dicetak ulang</div>
                </div>
              </div>
              <button onClick={() => { setShowCetakUlang(false); setNotaResult(null); }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', width: 32, height: 32, cursor: 'pointer', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
            </div>

            {/* Input Nomor Nota */}
            <div style={{ padding: '18px 22px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: '#f8fafc', border: `2px solid ${T.border}`, borderRadius: 10, padding: '0 14px', gap: 10 }}>
                  <i className="bi bi-upc-scan" style={{ color: '#94a3b8', fontSize: '1rem' }} />
                  <input
                    ref={notaInputRef}
                    value={inputNota}
                    onChange={e => setInputNota(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleFetchNota()}
                    placeholder="Ketik nomor nota lalu tekan Enter..."
                    style={{ flex: 1, border: 'none', background: 'transparent', padding: '11px 0', fontSize: '0.95rem', fontWeight: 600, outline: 'none', color: '#1e293b', fontFamily: 'monospace' }}
                  />
                </div>
                <button
                  onClick={handleFetchNota}
                  disabled={notaLoading}
                  style={{ background: `linear-gradient(135deg,${T.blue},${T.sky})`, color: '#fff', border: 'none', borderRadius: 10, padding: '11px 20px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, opacity: notaLoading ? 0.7 : 1 }}
                >
                  {notaLoading ? <><i className="bi bi-arrow-repeat" style={{ animation: 'spin .7s linear infinite' }} />Mencari...</> : <><i className="bi bi-search" />Cari Nota</>}
                </button>
              </div>
              <div style={{ marginTop: 6, fontSize: '0.74rem', color: '#94a3b8' }}>
                <i className="bi bi-info-circle me-1" />Contoh format: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, color: '#475569' }}>APT-070924-001</code>
              </div>
            </div>

            {/* Konten Nota */}
            {notaResult ? (
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

                {/* Info Header Nota */}
                <div style={{ padding: '14px 22px', background: '#f8fafc', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {[
                      { label: 'No. Nota',    val: notaResult.header.no_nota,        icon: 'bi-receipt' },
                      { label: 'Tanggal',     val: notaResult.header.tgl,            icon: 'bi-calendar2' },
                      { label: 'Pasien',      val: `${notaResult.header.no_medrec} / ${notaResult.header.nm_pasien}`, icon: 'bi-person' },
                      { label: 'Klasifikasi', val: notaResult.header.nm_klasifikasi, icon: 'bi-shield-check' },
                      { label: 'Kelas',       val: notaResult.header.nm_klas,        icon: 'bi-door-open' },
                      { label: 'Dokter',      val: notaResult.header.nm_dokter,      icon: 'bi-person-video3' },
                      { label: 'No. Resep',   val: notaResult.header.no_resep,       icon: 'bi-file-medical' },
                    ].map((f, i) => (
                      <div key={i} style={{ minWidth: 130 }}>
                        <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
                          <i className={`bi ${f.icon} me-1`} />{f.label}
                        </div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>{f.val || '-'}</div>
                      </div>
                    ))}
                    {notaResult.header.flag_print === 'X' && (
                      <div style={{ alignSelf: 'flex-end' }}>
                        <span style={{ background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
                          <i className="bi bi-printer-fill me-1" />Sudah Pernah Dicetak
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tabel Detail Obat */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.79rem' }}>
                    <thead>
                      <tr style={{ background: `linear-gradient(90deg,${T.teal},${T.tealM})`, color: '#fff' }}>
                        <th style={{ padding: '8px 10px', textAlign: 'center', width: '4%' }}>No.</th>
                        <th style={{ padding: '8px 10px', width: '35%' }}>Nama Obat</th>
                        <th style={{ padding: '8px 8px', textAlign: 'center', width: '8%' }}>Qty</th>
                        <th style={{ padding: '8px 8px', textAlign: 'center', width: '9%' }}>Satuan</th>
                        <th style={{ padding: '8px 8px', textAlign: 'center', width: '9%' }}>Dijamin</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right', width: '13%' }}>Total [Rp]</th>
                        <th style={{ padding: '8px 10px', width: '22%' }}>Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notaResult.items.map((item, idx) => (
                        <React.Fragment key={idx}>
                          <tr style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: `1px solid ${T.border}` }}>
                            <td style={{ padding: '6px 10px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>{idx + 1}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600, color: '#1e293b' }}>
                              {item.nm_obat}
                              {item.prinsipal && <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>{item.prinsipal}</div>}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>{fmt(item.qty)}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', color: '#64748b' }}>{item.satuan}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <span style={{
                                background: item.dijamin_label === 'Ya' ? '#dcfce7' : '#fef2f2',
                                color:      item.dijamin_label === 'Ya' ? '#16a34a' : '#dc2626',
                                borderRadius: 20, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700
                              }}>{item.dijamin_label}</span>
                            </td>
                            <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: T.teal }}>{fmt(item.total)}</td>
                            <td style={{ padding: '6px 10px', fontSize: '0.75rem', color: '#64748b' }}>
                              {(item.selisih > 0 || item.subsidi > 0) && (
                                <>
                                  <div style={{ color: '#16a34a', fontWeight: 600 }}>Dijamin: {item.nm_obat2 || '-'} = +{fmt(item.total2)}</div>
                                  <div style={{ color: '#92400e' }}>
                                    {item.selisih > 0 ? `Selisih bayar: ${fmt(item.selisih)}` : `Disubsidi: ${fmt(item.subsidi)}`}
                                  </div>
                                </>
                              )}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer Totals */}
                <div style={{ background: T.navy, padding: '10px 22px', flexShrink: 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 360, marginLeft: 'auto' }}>
                    {notaResult.header.admin > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fde68a', fontSize: '0.8rem' }}>
                        <span>Administrasi Resep</span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmt(notaResult.header.admin)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#fca5a5', fontSize: '0.9rem', fontWeight: 800, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 6 }}>
                      <span>TOTAL HARUS DIBAYAR</span>
                      <span style={{ fontFamily: 'monospace' }}>Rp {fmt(notaResult.grand_total)}</span>
                    </div>
                  </div>
                </div>

              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, padding: 40, color: '#94a3b8' }}>
                <i className="bi bi-receipt-cutoff" style={{ fontSize: '3rem', opacity: 0.2 }} />
                <span style={{ fontSize: '0.85rem' }}>Masukkan nomor nota lalu tekan Enter atau klik Cari Nota</span>
              </div>
            )}

            {/* Footer Buttons */}
            <div style={{ padding: '12px 22px', borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
                {notaResult && <><i className="bi bi-check-circle-fill text-success me-1" />{notaResult.items.length} item obat ditemukan</>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setShowCetakUlang(false); setNotaResult(null); }} style={{ background: '#f1f5f9', border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 16px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', color: '#64748b' }}>
                  <i className="bi bi-x-lg me-1" />Tutup
                </button>
                {notaResult && (
                  <button
                    onClick={handleCetakUlangPrint}
                    style={{ background: `linear-gradient(135deg,${T.teal},${T.tealM})`, color: '#fff', border: 'none', borderRadius: 9, padding: '8px 20px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}
                  >
                    <i className="bi bi-printer-fill" />Cetak Nota
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ─ MODAL CARI OBAT ─ */}
      <Modal show={showCariObat} onHide={() => setShowCariObat(false)} size="xl" centered>
        <div style={{ background: `linear-gradient(135deg,${T.teal},${T.tealM})`, padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.8px' }}>
            <i className="bi bi-capsule me-2" style={{ color: '#dcfce7' }}></i>
            PENCARIAN KATALOG OBAT
          </div>
          <button onClick={() => setShowCariObat(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer' }}>&times;</button>
        </div>
        <Modal.Body style={{ background: '#f8fafc', padding: '16px 20px' }}>
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
            <FormControl 
              placeholder="Ketik Nama Obat atau Kode lalu tekan Enter untuk mencari..." 
              value={obatSearchModal}
              onChange={handleObatSearch}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  fetchObatData(obatSearchModal);
                }
              }}
              style={{ paddingLeft: 44, height: 40, borderRadius: 8, fontSize: '0.9rem', border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: '60vh', overflowY: 'auto', borderRadius: '8px', border: `1px solid ${T.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: '#fff' }}>
            <Table hover responsive className="mb-0" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: T.slate, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <tr>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>KODE</th>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>NAMA OBAT</th>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>SATUAN</th>
                  <th className="py-2 px-2" style={{ fontWeight: 700, color: '#475569' }}>PABRIK</th>
                  <th className="py-2 px-2 text-end" style={{ fontWeight: 700, color: '#475569' }}>HARGA</th>
                  <th className="py-2 px-2 text-center" style={{ fontWeight: 700, color: '#475569', width: 80 }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {obatLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-muted">
                      <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                      Memuat katalog obat...
                    </td>
                  </tr>
                ) : obatFiltered.length > 0 ? (
                  obatFiltered.map((o, idx) => (
                    <tr key={idx} style={{ verticalAlign: 'middle', borderBottom: `1px solid ${T.border}` }}>
                      <td className="px-2 py-1 fw-bold text-primary" style={{ fontFamily: 'monospace' }}>{o.kdbr || o.kd_obat}</td>
                      <td className="px-2 py-1 fw-bold text-dark">{o.nama || o.nm_obat}</td>
                      <td className="px-2 py-1 text-secondary">{o.satuanbeli || o.satuan || '-'}</td>
                      <td className="px-2 py-1 text-secondary">{o.pabrik || '-'}</td>
                      <td className="px-2 py-1 text-end fw-bold" style={{ fontFamily: 'monospace', color: '#10b981' }}>{fmt(o.hda || o.harga || 0)}</td>
                      <td className="px-2 py-1 text-center">
                        <button 
                          style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => handleSelectObat(o)}
                        >
                          <i className="bi bi-check2 me-1"></i>Pilih
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      <i className="bi bi-box-seam" style={{ fontSize: '2.5rem', opacity: 0.2, display: 'block', marginBottom: 8 }}></i>
                      Tidak ada obat ditemukan.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ─ MODAL CARI PASIEN (Identik dengan TrsLaboratorium) ─ */}
      <Modal show={showCariPasien} onHide={() => setShowCariPasien(false)} size="xl" centered>
        <div style={{ background: `linear-gradient(135deg,${T.navy},${T.blue})`, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.8px' }}>
            <i className="bi bi-search me-2" style={{ color: T.sky }}></i>
            PENCARIAN DATA PASIEN {sumberPasien ? `- ${sumberPasien.label}` : ''}
          </div>
          <button onClick={() => setShowCariPasien(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer' }}>&times;</button>
        </div>
        <Modal.Body style={{ background: '#f8fafc', padding: '20px 24px' }}>
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
            <FormControl 
              placeholder="Ketik Nama atau No. KIRS untuk mencari..." 
              value={pasienSearchModal}
              onChange={handlePasienSearch}
              style={{ paddingLeft: 44, height: 44, borderRadius: 8, fontSize: '0.9rem', border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
              autoFocus
            />
          </div>

          <div style={{ maxHeight: '60vh', overflowY: 'auto', borderRadius: '8px', border: `1px solid ${T.border}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', background: '#fff' }}>
            <Table hover responsive className="mb-0" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: T.slate, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
                <tr>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>KIRS</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>NAMA PASIEN</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>DOKTER / PENGIRIM</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>RUANG / KELAS</th>
                  <th className="py-3 px-3" style={{ fontWeight: 700, color: '#475569' }}>JAMINAN</th>
                  <th className="py-3 px-3 text-center" style={{ fontWeight: 700, color: '#475569', width: 90 }}>AKSI</th>
                </tr>
              </thead>
              <tbody>
                {pasienLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                      Memuat data pasien...
                    </td>
                  </tr>
                ) : pasienFiltered.length > 0 ? (
                  pasienFiltered.map((p, idx) => (
                    <tr key={idx} style={{ verticalAlign: 'middle', borderBottom: `1px solid ${T.border}` }}>
                      <td className="px-3 fw-bold text-primary">{p.no_medrec || p.kirs}</td>
                      <td className="px-3 fw-bold text-dark">{p.nama}</td>
                      <td className="px-3 text-secondary">{p.nama_dok || p.nama_dokter || p.dokter || '-'}</td>
                      <td className="px-3 text-secondary">{p.no_kamar || p.ruangRawat || p.nm_ruang || p.ruang || '-'}</td>
                      <td className="px-3">
                        <span style={{ background: '#e0e7ff', color: '#4f46e5', padding: '4px 10px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700 }}>
                          {p.nm_klasifikasi || p.kd_klasifikasi || p.penjamin || p.klasifikasi || '-'}
                        </span>
                      </td>
                      <td className="px-3 text-center">
                        <button 
                          style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => handleSelectPasien(p)}
                        >
                          <i className="bi bi-check2 me-1"></i>Pilih
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      <i className="bi bi-person-x" style={{ fontSize: '2.5rem', opacity: 0.2, display: 'block', marginBottom: 8 }}></i>
                      Tidak ada data pasien ditemukan.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
      </Modal>

      {/* ─ MODAL PROSES / KONFIRMASI ─ */}
      {showProses && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, boxShadow: '0 25px 60px rgba(0,0,0,0.35)', padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a', fontSize: '1.5rem' }}>
                <i className="bi bi-gear-fill" />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a' }}>Konfirmasi Proses Transaksi</div>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 2 }}>Periksa kembali sebelum menyimpan</div>
              </div>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: '0.82rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Pasien</span><span style={{ fontWeight: 700, color: '#1e293b' }}>{pasien.nama}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Jenis Transaksi</span><span style={{ fontWeight: 700, color: '#1e293b' }}>{jenis?.label || '-'}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Jumlah Item</span><span style={{ fontWeight: 700, color: '#1e293b' }}>{rows.length} item</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 8 }}><span style={{ color: '#475569', fontWeight: 700 }}>GRAND TOTAL</span><span style={{ fontFamily: 'monospace', fontWeight: 900, color: T.teal, fontSize: '1rem' }}>Rp {fmt(grandTotal)}</span></div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowProses(false)} style={{ background: '#f1f5f9', border: `1px solid ${T.border}`, borderRadius: 9, padding: '8px 18px', fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer', color: '#64748b' }}>Batal</button>
              <button
                onClick={() => { setShowProses(false); showToast('Transaksi berhasil diproses! (Backend belum terhubung)', 'success'); }}
                style={{ background: `linear-gradient(135deg,${T.teal},${T.tealM})`, border: 'none', borderRadius: 9, padding: '8px 22px', fontSize: '0.83rem', fontWeight: 700, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <i className="bi bi-check-lg" />Proses Transaksi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─ MODAL DIALOG ALERT (Bootstrap) ─ */}
      <Modal show={dialog.show} onHide={() => setDialog(d => ({ ...d, show: false }))} centered size="sm" backdrop="static">
        <Modal.Body className="text-center p-4">
          <i className="bi bi-exclamation-circle-fill" style={{ fontSize: '3rem', color: '#f59e0b', marginBottom: '16px', display: 'block' }} />
          <h5 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>{dialog.title}</h5>
          <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '24px' }}>{dialog.msg}</p>
          <button 
            onClick={() => setDialog(d => ({ ...d, show: false }))} 
            style={{ width: '100%', background: `linear-gradient(135deg,${T.sky},#0284c7)`, border: 'none', borderRadius: '8px', padding: '10px', fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}
          >
            OK Mengerti
          </button>
        </Modal.Body>
      </Modal>

      {/* ─ MODAL CONFIRM (Bootstrap) ─ */}
      <Modal show={confirmDialog.show} onHide={() => setConfirmDialog({ show: false, msg: '', onConfirm: null })} centered size="sm" backdrop="static">
        <Modal.Body className="text-center p-4">
          <i className="bi bi-question-circle-fill" style={{ fontSize: '3rem', color: T.sky, marginBottom: '16px', display: 'block' }} />
          <h5 style={{ fontWeight: 800, color: '#0f172a', marginBottom: '12px' }}>Konfirmasi</h5>
          <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '24px' }}>{confirmDialog.msg}</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button 
              onClick={() => setConfirmDialog({ show: false, msg: '', onConfirm: null })} 
              style={{ flex: 1, background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '0.9rem', fontWeight: 700, color: '#475569' }}
            >
              Batal
            </button>
            <button 
              onClick={() => {
                if (confirmDialog.onConfirm) confirmDialog.onConfirm();
              }} 
              style={{ flex: 1, background: '#ef4444', border: 'none', borderRadius: '8px', padding: '10px', fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}
            >
              Ya, Lanjutkan
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ─ MODAL LAPORAN ─ */}
      {laporanDialog.show && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 24, width: 480, padding: 32, boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, background: 'linear-gradient(135deg,#d1fae5,#a7f3d0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#059669', fontSize: '1.5rem' }}>
                <i className="bi bi-graph-up-arrow" />
              </div>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.2rem', color: '#0f172a' }}>Laporan Harian Apotek</div>
                <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 2 }}>Konfigurasi parameter cetak laporan</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
              <input type="date" value={laporanDialog.tgl1} onChange={e => setLaporanDialog(p => ({ ...p, tgl1: e.target.value }))} className="form-control" style={{ borderRadius: 10, border: '1.5px solid #e2e8f0', fontWeight: 600 }} />
              <i className="bi bi-arrow-right" style={{ color: '#94a3b8' }} />
              <input type="date" value={laporanDialog.tgl2} onChange={e => setLaporanDialog(p => ({ ...p, tgl2: e.target.value }))} className="form-control" style={{ borderRadius: 10, border: '1.5px solid #e2e8f0', fontWeight: 600 }} />
            </div>
            {printProgress > 0 && laporanDialog.loading && (
              <div style={{ marginBottom: 20 }}>
                <ProgressBar animated now={printProgress} label={`${printProgress}%`} variant="success" style={{ height: '1.2rem', borderRadius: 8 }} />
                <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 6, fontWeight: 600 }}>Menyusun laporan...</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
              <button onClick={() => setLaporanDialog(p => ({ ...p, show: false }))} className="btn btn-light" style={{ padding: '0.6rem 1.5rem', fontWeight: 700, borderRadius: 12, border: `1.5px solid ${T.border}` }}>Batal</button>
              <button onClick={handleCetakLaporan} disabled={laporanDialog.loading} className="btn" style={{ padding: '0.6rem 2rem', fontWeight: 700, borderRadius: 12, background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                {laporanDialog.loading ? <i className="bi bi-arrow-repeat" style={{ animation: 'spin .8s linear infinite' }} /> : <><i className="bi bi-printer" />Cetak Laporan</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─ MODAL PDF VIEWER ─ */}
      <Modal show={!!pdfUrl} onHide={() => setPdfUrl('')} size="xl" centered backdrop="static">
        <div style={{ background: '#1e293b', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
            <i className="bi bi-file-earmark-pdf-fill me-2" style={{ color: '#ef4444' }} />Pratinjau Laporan Apotek
          </div>
          <button onClick={() => setPdfUrl('')} style={{ background: 'transparent', border: 'none', color: '#cbd5e1', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        <Modal.Body style={{ height: '80vh', padding: 0, background: '#f1f5f9' }}>
          {pdfUrl && <iframe src={pdfUrl} width="100%" height="100%" style={{ border: 'none' }} title="PDF Viewer" />}
        </Modal.Body>
      </Modal>

    </div>
  );
}
